/**
 * Import Monitor — Order Lookup tab.
 *
 * Search an order number and see what the ERP feed did to it, what it looks
 * like in Helix now, and whether a writeback is holding a field against the
 * feed. Replaces the Debug tab, which only had payloads for failed and
 * sampled rows — so a cleanly-imported order showed nothing, which is exactly
 * the case you get handed when someone says "the portal isn't showing my
 * change".
 */
import { Fragment, useCallback, useState } from 'react';
import { Loader2, Search, AlertTriangle, Inbox, Lock } from 'lucide-react';
import { CUSTOMER_REQUIRED_DATE, ORIGINAL_REQUIRED_DATE, SUPPLIER_PROMISE_DATE } from '../../config/dateLabels';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  importsAPI,
  type OrderLookupResponse,
  type OrderLookupImport,
  type OrderLookupLine,
} from '../../lib/api/imports';

const ACTION_STYLES: Record<string, string> = {
  created: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  updated: 'bg-blue-100 text-blue-800 border-blue-200',
  skipped: 'bg-slate-100 text-slate-700 border-slate-200',
  unallocated: 'bg-amber-100 text-amber-800 border-amber-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
};

/** Fields people chase when a change looks missing, in the order they check. */
const LINE_FIELDS: { key: keyof OrderLookupLine; label: string; date?: boolean }[] = [
  // The Customer Required Date is the CURRENT (re-planned) date; the ODD
  // below it is the originally required one, falling back to this when absent.
  // Both keys are canonical — half-moving them is what broke the per-cell
  // writeback-hold marker, which matches the backend's `field` string against
  // these keys (see `_held_fields` in import_service/views/order_lookup.py).
  { key: 'customer_required_date', label: CUSTOMER_REQUIRED_DATE, date: true },
  { key: 'original_required_date', label: ORIGINAL_REQUIRED_DATE, date: true },
  { key: 'supplier_promise_date', label: SUPPLIER_PROMISE_DATE, date: true },
  { key: 'supplier_proposed_date', label: 'Proposed date', date: true },
  { key: 'quantity', label: 'Qty ordered' },
  { key: 'received_qty', label: 'Qty received' },
  { key: 'unit_price', label: 'Unit price' },
  { key: 'status', label: 'Status' },
];

const FIELD_LABELS: Record<string, string> = {
  customer_required_date: CUSTOMER_REQUIRED_DATE,
  delivery_date: CUSTOMER_REQUIRED_DATE,  // deprecated alias, one release
  unit_price: 'Unit price',
  quantity: 'Quantity',
};

function fmt(value: unknown, isDate = false): string {
  if (value === null || value === undefined || value === '') return '—';
  if (isDate) {
    const d = new Date(String(value));
    return isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-GB');
  }
  return String(value);
}

function fmtWhen(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function ImportRow({ row }: { row: OrderLookupImport }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b last:border-0 py-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${ACTION_STYLES[row.action] || ACTION_STYLES['skipped']}`}>
            {row.action}
          </span>
          <span className="font-medium">Line {row.line_number || '—'}</span>
          <span className="text-muted-foreground text-xs">{row.data_type.replace(/_/g, ' ')}</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {fmtWhen(row.job_created_at)}{row.source_row ? ` · row ${row.source_row}` : ''}
        </span>
      </div>

      {row.error_message && (
        <p className={`text-xs mt-1.5 ${row.action === 'failed' ? 'text-red-700' : 'text-muted-foreground'}`}>
          {row.error_code ? <span className="font-mono mr-1">{row.error_code}</span> : null}
          {row.error_message}
        </p>
      )}

      {row.fields_changed && Object.keys(row.fields_changed).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {Object.keys(row.fields_changed).map(f => (
            <Badge key={f} variant="outline" className="text-xs font-normal">{f}</Badge>
          ))}
        </div>
      )}

      {row.raw_record ? (
        <>
          <button
            onClick={() => setOpen(!open)}
            className="mt-2 text-xs text-primary hover:underline"
          >
            {open ? 'hide payload as received' : 'view payload as received'}
          </button>
          {open && (
            <pre className="mt-2 p-3 rounded bg-muted text-xs overflow-x-auto">
              {JSON.stringify(row.raw_record, null, 2)}
            </pre>
          )}
        </>
      ) : (
        // An absent payload means it was not kept, not that the ERP sent
        // nothing. Saying so stops this reading as "no data received". Every
        // row keeps its payload since CR-8675; rows imported before that (when
        // created and updated rows were sampled) can still have none.
        <p className="mt-2 text-xs text-muted-foreground italic">
          Payload not retained for this row (recorded before every row kept its payload).
        </p>
      )}
    </div>
  );
}

/**
 * Order lookup for one customer.
 *
 * @param customerId - Customer whose orders to search
 */
export default function ImportOrderLookupTab({ customerId }: { customerId: number }) {
  const [term, setTerm] = useState('');
  const [lineTerm, setLineTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<OrderLookupResponse | null>(null);

  const search = useCallback(async () => {
    const order = term.trim();
    if (!order || !customerId) return;
    setLoading(true);
    setResult(null);
    try {
      setResult(await importsAPI.getOrderLookup(order, customerId, lineTerm.trim() || undefined));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setLoading(false);
    }
  }, [term, lineTerm, customerId]);

  if (!customerId) {
    return (
      <Card>
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          Select a customer to look up an order.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Look up an order</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Every job the order appeared in, what changed on each line, and whether a
              pending writeback is currently holding a field against the ERP feed.
              Payloads are kept for every row for 7 days, and always for the last two
              pushes per data type.
            </p>
          </div>
          <div className="flex gap-2">
            <input
              id="import-order-lookup"
              name="order_no"
              aria-label="Order number"
              value={term}
              onChange={e => setTerm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void search(); }}
              placeholder="Order number, e.g. 351540 (or 351540/1)"
              className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-background
                         focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <input
              id="import-order-lookup-line"
              name="line_no"
              aria-label="Line number"
              value={lineTerm}
              onChange={e => setLineTerm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void search(); }}
              placeholder="Line (optional)"
              className="w-40 px-3 py-2 text-sm border border-border rounded-lg bg-background
                         focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <button
              onClick={() => void search()}
              disabled={loading || !term.trim()}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground
                         disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Search
            </button>
          </div>
        </CardContent>
      </Card>

      {result && !result.found && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-2">
          <Inbox className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">
              {result.order_no}{result.line_no ? ` / line ${result.line_no}` : ''} not found
            </p>
            <p className="text-amber-800 mt-1">
              {result.line_no
                ? 'No such line on this order for this customer, and nothing in the import history either. Check the line number, or search the order without a line.'
                : 'No purchase order with this number for this customer, and nothing in the import history either. Check the number, or whether it belongs to a different customer.'}
            </p>
          </div>
        </div>
      )}

      {result?.found && (
        <>
          {/* The answer to "why hasn't my change shown up" more often than not. */}
          {result.held_fields.length > 0 && (
            <div className="rounded-lg border border-blue-300 bg-blue-50 p-4 flex items-start gap-2">
              <Lock className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-blue-900">
                  Fields held against the ERP feed
                </p>
                <p className="text-blue-800 mt-1 text-xs">
                  A change of ours is still on its way to the ERP, so the import is
                  deliberately refusing the ERP&apos;s value for these fields until it lands.
                  This is working as designed — not a dropped update.
                </p>
                <ul className="mt-2 space-y-1">
                  {result.held_fields.map((h, i) => (
                    <li key={i} className="text-xs text-blue-900">
                      <strong>Line {h.line_number} · {FIELD_LABELS[h.field] || h.field}</strong>
                      {' — '}{h.reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {result.po && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {result.po.po_number}
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    {result.po.supplier_name || '—'} · {result.po.status} · {result.po.currency || '—'} ·{' '}
                    {result.po.line_count} line{result.po.line_count === 1 ? '' : 's'}
                    {result.line_no ? ` · showing line ${result.line_no} only` : ''}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                        <th className="py-2 pr-4 font-medium">Line</th>
                        <th className="py-2 pr-4 font-medium">Part</th>
                        {LINE_FIELDS.map(f => (
                          <th key={String(f.key)} className="py-2 pr-4 font-medium whitespace-nowrap">{f.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.lines.map(line => {
                        const heldOnLine = result.held_fields.filter(h => h.line_number === line.line_number);
                        return (
                          <Fragment key={line.line_number}>
                          <tr className="border-b last:border-0">
                            <td className="py-2.5 pr-4 font-medium">{line.line_number}</td>
                            <td className="py-2.5 pr-4 whitespace-nowrap">{line.part_number || '—'}</td>
                            {LINE_FIELDS.map(f => {
                              const isHeld = heldOnLine.some(h =>
                                h.field === f.key
                                || (h.field === 'quantity' && f.key === 'quantity')
                                || (h.field === 'unit_price' && f.key === 'unit_price'));
                              return (
                                <td key={String(f.key)} className="py-2.5 pr-4 whitespace-nowrap tabular-nums">
                                  <span className={isHeld ? 'text-blue-700 font-medium' : ''}>
                                    {fmt(line[f.key], f.date)}
                                  </span>
                                  {isHeld && <Lock className="inline h-3 w-3 ml-1 text-blue-600" />}
                                </td>
                              );
                            })}
                          </tr>
                          {(line.schedules?.length ?? 0) > 0 && (
                            <tr className="border-b last:border-0 bg-muted/20">
                              <td />
                              <td colSpan={LINE_FIELDS.length + 1} className="py-2 pr-4">
                                <div className="text-xs font-semibold text-muted-foreground mb-1">
                                  Schedule slots ({line.schedules.length})
                                </div>
                                <table className="text-xs w-full max-w-3xl">
                                  <thead>
                                    <tr className="text-left text-muted-foreground">
                                      <th className="pr-3 font-medium">Slot</th>
                                      <th className="pr-3 font-medium">Date</th>
                                      <th className="pr-3 font-medium text-right">Qty</th>
                                      <th className="pr-3 font-medium text-right">Shipped</th>
                                      <th className="pr-3 font-medium">Status</th>
                                      <th className="font-medium">Latest ERP change</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {line.schedules.map(s => (
                                      <tr key={s.schedule_line_number}>
                                        <td className="pr-3 tabular-nums">{s.schedule_line_number}</td>
                                        <td className="pr-3 whitespace-nowrap">{s.schedule_date ? fmt(s.schedule_date, true) : '—'}</td>
                                        <td className="pr-3 text-right tabular-nums">{Number(s.schedule_qty).toLocaleString()}</td>
                                        <td className="pr-3 text-right tabular-nums">{Number(s.shipped_qty).toLocaleString()}</td>
                                        <td className={`pr-3 ${s.status === 'cancelled' ? 'text-red-600 font-medium' : ''}`}>{s.status}</td>
                                        <td className="text-muted-foreground">
                                          {s.changes[0]
                                            ? `${s.changes[0].field}: ${s.changes[0].old ?? '—'} → ${s.changes[0].new ?? '—'} (${s.changes[0].reason ?? 'no reason'})`
                                            : '—'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {(result.writebacks.pending.length > 0
            || result.writebacks.succeeded.length > 0
            || result.writebacks.failed.length > 0) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Writebacks for this order</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3 text-sm">
                <Badge variant="outline" className="text-amber-700 border-amber-200">
                  {result.writebacks.pending.length} pending
                </Badge>
                <Badge variant="outline" className="text-emerald-700 border-emerald-200">
                  {result.writebacks.succeeded.length} written back
                </Badge>
                <Badge variant="outline" className="text-red-700 border-red-200">
                  {result.writebacks.failed.length} failed
                </Badge>
                <span className="text-xs text-muted-foreground self-center">
                  Full detail on the Writeback tab.
                </span>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Import history
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  {result.import_count} row{result.import_count === 1 ? '' : 's'}
                  {result.imports_truncated ? ' (showing the most recent 100)' : ''}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {result.imports.length === 0 ? (
                <div className="flex items-start gap-2 py-6">
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium">This order exists in Helix but the feed has not touched it</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      No import log rows. Either it predates the retained logs, or the ERP
                      has stopped sending it — in which case nothing arriving is the finding.
                    </p>
                  </div>
                </div>
              ) : (
                <div>{result.imports.map(row => <ImportRow key={row.id} row={row} />)}</div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
